import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { experimental } from '@trp-beacon-ui-kit/react-theme/warnings';
import { FormContext } from '../Form';
import styles from './styles.css';

const NOOP = () => {};

const kebab = (str) => str.toLowerCase().replace(/[^0-9a-zA-Z]+/gm, '-');

const getInputValue = (wrapper, name) => {
  const elem = wrapper.querySelector(`input[data-name="${name}"]:checked`);
  return elem ? elem.value : false;
};

const unique = () =>
  Math.random()
    .toString(36)
    .substring(7);

const runFns = (...fns) => (ev) => fns.map((fn = NOOP) => fn(ev));

function RadioGroupField({ title, required, name, options, horizontal, onChange }) {
  const namespace = useMemo(unique, []);
  const namespacedName = `radio-${namespace}-${kebab(name)}`;
  const makeID = (optionTitle) => `${namespacedName}-${kebab(optionTitle)}`;

  const formContext = useContext(FormContext);
  const wrapperRef = useRef(null);
  const [value, setValue] = useState();
  const [touched, setTouched] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const touch = () => setTouched(true);

  const validate = () => {
    const { current: wrapper } = wrapperRef;
    if (!wrapper) return;

    const val = getInputValue(wrapper, name);
    setValue(val);

    setErrorMessage(required && !val ? 'Required' : null);
  };

  const validateAndTouch = () => {
    validate();
    touch();
  };

  useEffect(() => {
    validate();
  }, []);

  useEffect(() => {
    if (formContext) {
      const { registerInput, registerSubmitHook, unregisterSubmitHook, removeField } = formContext;

      registerInput(name, errorMessage, value);
      registerSubmitHook(validateAndTouch);
      return () => {
        removeField(name);
        unregisterSubmitHook(validateAndTouch);
      };
    }
    return NOOP;
  });

  const error = touched && errorMessage;
  return (
    <div className="wrapper beacon-container">
      <fieldset ref={wrapperRef} className={cn({ horizontal, vertical: !horizontal, error })}>
        <legend>
          {title} {required && <span>(Required)</span>}
        </legend>
        <div className="options">
          {options.map(({ title: optionTitle, ...restOfOption }) => {
            const optionID = makeID(optionTitle);
            return (
              <div className="option" key={optionID}>
                <label htmlFor={optionID}>{optionTitle}&nbsp;</label>
                <input
                  id={optionID}
                  title={optionTitle}
                  type="radio"
                  onChange={runFns(onChange, validate)}
                  name={namespacedName}
                  data-name={name}
                  {...restOfOption}
                />
              </div>
            );
          })}
        </div>
      </fieldset>

      {error && <div className="error-msg">{errorMessage}</div>}

      <style jsx>{styles}</style>
    </div>
  );
}

RadioGroupField.defaultProps = {
  required: false,
  horizontal: false,
};

RadioGroupField.propTypes = {
  horizontal: PropTypes.bool,
  title: PropTypes.string.isRequired,
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      defaultChecked: PropTypes.bool,
    }),
  ).isRequired,
};

export default experimental(RadioGroupField);
